import { IconAlertCircle, IconPlus } from '@tabler/icons-react'
import { Alert, Button, Stack } from 'components'
import { Layout } from 'components/Layout'
import { ArcPageShell } from 'components/ArcPageShell'
import { CurrentUserContextProvider, useCurrentUser } from 'context'
import type { FC } from 'react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { useArcGrades } from '../ArcNewProject/components/AboutStep/hooks/useArcGrades'
import {
  LinearProgress,
  ProjectGrid,
  ProjectsTabs,
  ProjectsToolbar,
} from './components'
import { useArcProjects, useProjectsQueryParams, useCategories } from './hooks'
import { isDemoProjectCardId, useArcDiskDemoEnabled, withDemoProjects } from 'arcdisk'
import type { ArcProject } from './types'

const ArcProjectsContent: FC = () => {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()

  const {
    tab,
    status,
    search,
    orderBy,
    direction,
    page,
    gradeRange,
    categoryId,
    setTab,
    setStatus,
    setSearch,
    setPage,
    setGradeRange,
    setCategoryId,
  } = useProjectsQueryParams()

  const isLibrary = tab === 'library'
  const location = useLocation()

  const backlink = useMemo(() => {
    const params = new URLSearchParams(location.search)
    params.set('tab', isLibrary ? 'library' : 'my')
    const query = params.toString()
    return query ? `/arc_projects?${query}` : '/arc_projects'
  }, [location.search, isLibrary])

  const { grades } = useArcGrades()
  const { categories } = useCategories()

  const categoryOptions = useMemo(
    () => categories.map(c => ({ value: c.id, label: c.name })),
    [categories],
  )

  const { projects: apiProjects, pagination, isLoading, isFetching, isError } = useArcProjects({
    tab,
    status,
    search,
    orderBy,
    direction,
    page,
    gradeRange: isLibrary ? gradeRange : undefined,
    categoryId: isLibrary ? categoryId : undefined,
  })

  // ArcDisk demo: splice the three demo project cards into page 1 of "My
  // projects" (demo mode only). See src/arcdisk/data/projects.ts.
  const arcDiskDemo = useArcDiskDemoEnabled()
  const projects = useMemo(
    () => (arcDiskDemo ? withDemoProjects(apiProjects, { tab, page, search, status }) : apiProjects),
    [arcDiskDemo, apiProjects, tab, page, search, status],
  )

  const profile = currentUser?.profile_used
  const isTutor = profile === 'tutor'
  const isManager = profile === 'manager'
  const isStudent = profile === 'student'

  const canCreateProject = isTutor || isManager
  const showTabs = !isStudent
  const excludeStatuses = isStudent ? ['draft' as const] : []

  const canEditProject = useCallback(
    (project: ArcProject): boolean => {
      // Demo cards are read-only: no edit/assign/delete menu
      if (isDemoProjectCardId(project.id)) return false
      if (isManager) return true
      if (!isTutor || !currentUser?.id) return false
      if (project.authorId === currentUser.id) return true
      const userEmail = currentUser.email || currentUser.username
      if (userEmail && project.coTeachers?.some(ct => ct.email === userEmail)) return true
      return false
    },
    [isManager, isTutor, currentUser],
  )

  return (
    <ArcPageShell
      title={t('arc_projects.title')}
      actions={
        canCreateProject && (
          <Button
            component={Link}
            to='/projects/new'
            variant='filled'
            color='teal'
            size='md'
            leftIcon={<IconPlus size={18} />}
          >
            {t('arc_projects.new_project')}
          </Button>
        )
      }
    >
      <Stack spacing="md">
        <ProjectsTabs value={tab} onChange={setTab} showTabs={showTabs} />

        <ProjectsToolbar
          search={search}
          status={status}
          showStatusChips={!isLibrary}
          showGradeFilter={isLibrary}
          showCategoryFilter={isLibrary}
          grades={grades}
          gradeRange={gradeRange}
          categories={categoryOptions}
          categoryId={categoryId}
          excludeStatuses={excludeStatuses}
          onSearchChange={setSearch}
          onStatusChange={setStatus}
          onGradeRangeChange={setGradeRange}
          onCategoryChange={setCategoryId}
        />

        {isError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {t('arc_projects.error_loading')}
          </Alert>
        )}

        <LinearProgress visible={isFetching && !isLoading} />

        <ProjectGrid
          projects={projects}
          isLoading={isLoading}
          isLibrary={isLibrary}
          canEdit={canEditProject}
          pagination={pagination}
          onPageChange={setPage}
          backlink={backlink}
        />
      </Stack>
    </ArcPageShell>
  )
}

export const ArcProjectsPage: FC = () => {
  return (
    <CurrentUserContextProvider>
      <Layout>
        <ArcProjectsContent />
      </Layout>
    </CurrentUserContextProvider>
  )
}
